import axios from 'axios';

import {getAdResponse} from '@jobseeker/common-core/utils';
import type {AdsElementType, AdsOptions} from '@jobseeker/common-core/types';
import courseService from '@jobseeker/common-core/services/course.service';

const sortByOrder = (ads: AdsElementType[]) => {
  if (ads && ads.some(adElement => 'ordering' in adElement)) {
    return ads.sort(
      (a: any, b: any) => parseInt(a.ordering, 10) - parseInt(b.ordering, 10)
    );
  }
  return ads;
};

const buildCourseParams = (options?: AdsOptions): any => {
  const params: any = {
    imageType: 'webp',
    ...(options?.courseId && {courseId: options.courseId}),
  };

  if (options?.categoryIds) {
    params['categoryIds'] =
      options.categoryIds === 'jobfeed' ? '' : options.categoryIds;
  }

  if (options?.page !== undefined) {
    params['page'] = options.page;
  }

  if (options?.size) {
    params['size'] = options.size;
  }

  if (options?.shuffle) {
    params['shuffle'] = options.shuffle;
  }

  if (options?.orderby) {
    params['orderby'] = options.orderby;
  }

  return params;
};

const processAdList = (adList: any[], options?: AdsOptions): AdsElementType[] => {
  if (!adList || adList.length === 0) {
    return [];
  }

  let adListFinal = adList;
  
  if (options?.categoryIds === 'jobfeed') {
    adListFinal = adList.filter(
      (j: any) => j?.metadata[0]?.['Allowed on JobFeeds'] === 1
    );
  }

  if (options?.onRotation) {
    if (adListFinal.length > 1) {
      const currentDate = new Date();
      const day = currentDate.getDate();
      const index = day % 2 === 0 ? 1 : 0;
      adListFinal = [adListFinal[index]];
    }
  }

  let allAds: any = [];
  adListFinal.forEach(({metadata, id, categoryIds}: any) => {
    if (metadata?.length > 0) {
      metadata[0]['id'] = id;
    }
    allAds = allAds.concat(getAdResponse(metadata, categoryIds));
  });

  return sortByOrder(allAds);
};

const fetchAndProcessCourseAds = async (
  apiCall: () => Promise<any>,
  options?: AdsOptions,
  returnEntireResponse = false,
  errorContext = 'courses-list'
) => {
  try {
    const response = await apiCall();
    
    if (returnEntireResponse) {
      return response;
    }

    const adList = response.data?.adList;
    return processAdList(adList || [], options);
  } catch (error) {
    console.log(`🚀 ~ file: loadAds.ts ~ loadAds ~ ${errorContext} error:`, error);
    return '';
  }
};

const getCourseSlotConfig = (metaInfo: any) => {
  const ads = metaInfo?.ads || {};
  
  const config: Record<string, {
    apiCall: (params: any) => Promise<any>;
    context: string;
  }> = {};

  if (ads.learnGrow?.slot) {
    config[ads.learnGrow.slot] = {apiCall: courseService.getCoursesList, context: 'courses-list'};
  }

  if (ads.featuredInstitutes?.slot) {
    config[ads.featuredInstitutes.slot] = {apiCall: courseService.getFeaturedInstitutes, context: 'featured-institutes'};
  }

  if (ads.suggestedCourses?.slot) {
    config[ads.suggestedCourses.slot] = {apiCall: courseService.getCoursesAfterJobApply, context: 'job-ready-courses'};
  }

  if (ads.infeedCategory?.slot) {
    config[ads.infeedCategory.slot] = {apiCall: courseService.getInFeedCourses, context: 'in-feed-courses'};
  }

  if (ads.inFeed?.slot) {
    config[ads.inFeed.slot] = {apiCall: courseService.getInFeedCourses, context: 'in-feed-courses'};
  }

  if (ads.courseJobList && Array.isArray(ads.courseJobList)) {
    ads.courseJobList.forEach((course: any) => {
      if (course.slot) {
        config[course.slot] = {apiCall: courseService.getInFeedCourses, context: 'in-feed-courses'};
      }
    });
  }

  return config;
};

const loadAds = async (
  slotId: string,
  options?: AdsOptions,
  isCourseAds: boolean = false,
  returnEntireResponse = false,
  metaInfo?: any
) => {
  if (typeof window === 'undefined') {
    return [];
  }
  
  const courseSlotConfigMap = getCourseSlotConfig(metaInfo || {});
  const courseSlotConfig = courseSlotConfigMap[slotId];
  
  if (courseSlotConfig) {
    const params = buildCourseParams(options);
    return fetchAndProcessCourseAds(
      () => courseSlotConfig.apiCall({params}),
      options,
      returnEntireResponse,
      courseSlotConfig.context
    );
  }
  
  let API_PATH = process.env.NEXT_PUBLIC_API_ADS;
  if (isCourseAds) {
    API_PATH = API_PATH + 'courses/';
  }

  const params: AdsOptions = {
    imageType: 'webp',
    ...(options?.courseId && {courseId: options.courseId}),
  };

  if (options?.categoryIds) {
    params['categoryIds'] =
      options.categoryIds === 'jobfeed' ? '' : options.categoryIds;
  }

  if (options?.page) {
    params['page'] = options.page;
  }

  if (options?.size) {
    params['size'] = options.size;
  }

  if (options?.exp) {
    params['exp'] = options.exp;
  }

  if (options?.gender) {
    params['gender'] = options.gender;
  }

  if (options?.functionalArea) {
    params['functionalArea'] = options.functionalArea;
  }

  if (options?.user_location) {
    params['user_location'] = options.user_location;
  }

  if (options?.tags) {
    params['tags'] = options.tags;
  }

  if (options?.shuffle) {
    params['shuffle'] = options.shuffle;
  }

  if (options?.orderby) {
    params['orderby'] = options.orderby;
  }

  if (options?.jobTags) {
    params['jobTags'] = options.jobTags;
  }

  if (options?.courseTags) {
    params['courseTags'] = options.courseTags;
  }

  try {
    const response = await axios.get(`${API_PATH}${slotId}`, {
      params: params,
    });
    if (returnEntireResponse) {
      return response?.data;
    }
    const adList = response.data?.data?.adList;
    return processAdList(adList || [], options);
  } catch (error) {
    console.log('🚀 ~ file: loadAds.ts:27 ~ loadAds ~ error:', error);
    return '';
  }
};

export default loadAds;
